import React from 'react'
import { motion } from 'framer-motion'
import { SKIP_INTRO_HIDE_BEFORE_END_SECONDS } from '@/constants/video'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

interface HlsSkipIntroButtonProps {
  introStartTime: number
  introEndTime: number
  episodeGuid?: string
}

/**
 * Skip Intro button for the HLS.js player.
 * Appears when the current time is within the intro segment
 * (between introStartTime and introEndTime - 5s) and the video is playing.
 *
 * Uses HlsPlayerUIContext directly since AngelPlayerContext is not yet wired
 * to the HLS player (Phase 6). The Bitmovin player's equivalent reads from
 * AngelPlayerContext via SkipIntro.tsx / SkipIntroOverlay.tsx.
 */
export const HlsSkipIntroButton: React.FC<HlsSkipIntroButtonProps> = ({
  introStartTime,
  introEndTime,
  episodeGuid,
}) => {
  const track = useSafeTrack()
  const { currentTime, playing, controller } = useHlsPlayerUI()
  const { t } = useTranslate('common')

  const inIntro =
    playing && currentTime >= introStartTime && currentTime <= introEndTime - SKIP_INTRO_HIDE_BEFORE_END_SECONDS

  const handleSkip = () => {
    controller?.seek(introEndTime)
    track('Skipped Intro', { guid: episodeGuid })
  }

  return (
    <div className="absolute right-1 top-1 z-40 sm:bottom-24 sm:right-6 sm:top-auto">
      <motion.div animate={inIntro ? 'open' : 'closed'} variants={ANIMATION_VARIANTS}>
        <button type="button" onClick={handleSkip} className="overflow-hidden rounded-lg border-0 outline-none">
          <div className="flex items-center bg-oxide-bright px-3 py-2 transition-colors hover:brightness-110 sm:px-4 sm:py-4">
            <span className="photon-label-sm sm:photon-label-lg font-semibold text-black">
              {t('skipIntro', 'Skip Intro')}
            </span>
          </div>
        </button>
      </motion.div>
    </div>
  )
}

const ANIMATION_VARIANTS = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      bounce: 0.2,
      duration: 0.6,
    },
  },
  closed: { y: 40, opacity: 0, display: 'none' as const },
}
